import React from "react";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";
import formatDuration from "../../utils/formatDuration";

const useStyles = makeStyles(theme => ({
	card: {
		padding: theme.spacing(2),
		height: "100%",
		display: "flex",
		flexDirection: "column",
		justifyContent: "space-between",
	},
	value: {
		fontWeight: 700,
		marginTop: theme.spacing(1),
	},
	subtitle: {
		color: theme.palette.text.secondary,
		fontSize: "0.75rem",
		marginTop: theme.spacing(0.5),
	},
	highlight: {
		borderLeft: `4px solid ${theme.palette.primary.main}`,
	},
	ai: {
		borderLeft: `4px solid ${theme.palette.success.main}`,
	},
	warning: {
		borderLeft: `4px solid ${theme.palette.warning.main}`,
	},
}));

const MetricCard = ({ title, value, subtitle, className }) => {
	const classes = useStyles();
	return (
		<Paper className={`${classes.card} ${className || ""}`}>
			<Typography variant="body2" color="textSecondary">
				{title}
			</Typography>
			<Typography variant="h4" className={classes.value}>
				{value ?? 0}
			</Typography>
			{subtitle && (
				<Typography className={classes.subtitle}>{subtitle}</Typography>
			)}
		</Paper>
	);
};

const MetricsCards = ({ summary }) => {
	const classes = useStyles();
	if (!summary) return null;

	return (
		<Grid container spacing={2}>
			<Grid item xs={6} sm={4} md={3}>
				<MetricCard
					title={i18n.t("dashboard.metrics.open")}
					value={summary.open}
					subtitle={i18n.t("dashboard.metrics.realtime")}
					className={classes.highlight}
				/>
			</Grid>
			<Grid item xs={6} sm={4} md={3}>
				<MetricCard
					title={i18n.t("dashboard.metrics.pending")}
					value={summary.pending}
					subtitle={i18n.t("dashboard.metrics.realtime")}
					className={classes.warning}
				/>
			</Grid>
			<Grid item xs={6} sm={4} md={3}>
				<MetricCard
					title={i18n.t("dashboard.metrics.closedInPeriod")}
					value={summary.closedInPeriod}
				/>
			</Grid>
			<Grid item xs={6} sm={4} md={3}>
				<MetricCard
					title={i18n.t("dashboard.metrics.totalCreated")}
					value={summary.totalCreatedInPeriod}
				/>
			</Grid>
			<Grid item xs={6} sm={4} md={3}>
				<MetricCard
					title={i18n.t("dashboard.metrics.aiResolved")}
					value={summary.aiResolved}
					subtitle={i18n.t("dashboard.metrics.aiResolvedHint")}
					className={classes.ai}
				/>
			</Grid>
			<Grid item xs={6} sm={4} md={3}>
				<MetricCard
					title={i18n.t("dashboard.metrics.humanAttended")}
					value={summary.humanAttended}
				/>
			</Grid>
			<Grid item xs={6} sm={4} md={3}>
				<MetricCard
					title={i18n.t("dashboard.metrics.humanHandoffs")}
					value={summary.humanHandoffs}
					subtitle={i18n.t("dashboard.metrics.humanHandoffsHint")}
				/>
			</Grid>
			<Grid item xs={6} sm={4} md={3}>
				<MetricCard
					title={i18n.t("dashboard.metrics.aiActive")}
					value={summary.aiActive}
					subtitle={i18n.t("dashboard.metrics.realtime")}
					className={classes.ai}
				/>
			</Grid>
			<Grid item xs={6} sm={4} md={3}>
				<MetricCard
					title={i18n.t("dashboard.metrics.avgHumanTime")}
					value={formatDuration(summary.avgHumanResolutionSeconds)}
					subtitle={i18n.t("dashboard.metrics.avgHumanTimeHint")}
				/>
			</Grid>
			<Grid item xs={6} sm={4} md={3}>
				<MetricCard
					title={i18n.t("dashboard.metrics.avgAiTime")}
					value={formatDuration(summary.avgAiResolutionSeconds)}
					subtitle={i18n.t("dashboard.metrics.avgAiTimeHint")}
					className={classes.ai}
				/>
			</Grid>
		</Grid>
	);
};

export default MetricsCards;
